import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n";
import { Reveal, MaskedLine, Label } from "../components/Motion";
import { usePageMeta } from "../lib/seo";

const FACTORY_IMG = "https://images.unsplash.com/photo-1717386255767-52643970d483?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxmYWN0b3J5JTIwZmxvb3IlMjB3ZWxkaW5nJTIwYXNzZW1ibHl8ZW58MHx8fHwxNzg3MDM3MjM2fDA&ixlib=rb-4.1.0&q=85";

const GALLERY_IMAGES = [
  "/images/dept-fabrication.webp",
  "/images/gallery-frames.webp",
  "/images/gallery-tank.webp",
  "/images/gallery-frame-tall.webp",
  "/images/dept-assembly.webp",
  "/images/service-custom.webp",
  "/images/dept-cnc.webp",
  "/images/dept-turning.webp",
  "/images/dept-milling.webp",
  "/images/service-overhaul.webp",
  "/images/services-overhaul.webp",
  "/images/services-custom.webp",
  "/images/stairs.webp",
  "/images/gate.webp",
  "/images/gallery-mezzanine.webp",
  "/images/gallery-machine-detail.webp",
  "/images/gallery-pneumatics.webp",
];

export default function About() {
  const { t } = useLanguage();
  usePageMeta(t("meta.about.t"), t("meta.about.d"));

  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section data-testid="about-header" className="mx-auto max-w-7xl px-6 pb-16 pt-40 md:px-12 md:pb-24 md:pt-56">
        <MaskedLine delay={0.1}>
          <p className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">
            <span className="inline-block h-px w-12 bg-brand-yellow" />
            GTECH SRL
          </p>
        </MaskedLine>
        <h1 data-testid="about-title" className="font-display text-[clamp(2.8rem,9vw,8rem)] font-black leading-[0.95] tracking-tighter">
          <MaskedLine delay={0.25}>{t("about.title")}</MaskedLine>
          <MaskedLine delay={0.37}>
            <span className="text-brand-yellow">{t("about.titleAccent")}</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.55}>
          <p data-testid="about-sub" className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">
            {t("about.sub")}
          </p>
        </Reveal>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section data-testid="manifesto-section" className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {t("about.chapters").map((ch, i) => (
            <div
              key={i}
              data-testid={`manifesto-chapter-${i}`}
              className={`relative flex flex-col gap-6 border-b border-white/10 py-20 md:py-28 ${
                i % 2 === 1 ? "md:items-end md:text-right" : ""
              }`}
            >
              <span className={`pointer-events-none absolute -top-4 font-display text-[9rem] font-black leading-none text-white/5 md:text-[13rem] ${i % 2 === 1 ? "right-0" : "left-0"}`}>
                {ch.num}
              </span>
              <Reveal className="relative">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">{ch.num}</p>
                <h2 className="mt-4 font-display text-4xl font-bold tracking-tight md:text-6xl">{ch.title}</h2>
              </Reveal>
              <Reveal delay={0.15} className="relative">
                <p className="max-w-2xl text-base leading-relaxed text-neutral-400 md:text-lg">{ch.body}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* PARALLAX FACTORY */}
      <section data-testid="factory-parallax-section" ref={imgRef} className="relative h-[60vh] overflow-hidden md:h-[75vh]">
        <motion.img
          src={FACTORY_IMG}
          alt="GTECH factory floor"
          loading="lazy"
          style={{ y: imgY }}
          className="absolute inset-0 h-[124%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
      </section>

      {/* GALLERY */}
      <section data-testid="gallery-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <Reveal><Label>{t("about.gallery.label")}</Label></Reveal>
        <Reveal delay={0.1}>
          <h2 data-testid="gallery-title" className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
            {t("about.gallery.title")}
          </h2>
        </Reveal>
        <div className="mt-16 columns-2 gap-4 md:columns-3">
          {GALLERY_IMAGES.map((src, i) => (
            <Reveal key={i} delay={0.04 * i} className="mb-4 break-inside-avoid">
              <div data-testid={`gallery-item-${i}`} className="group overflow-hidden border border-brand-line">
                <img
                  src={src}
                  alt="GTECH SRL — workshop"
                  loading="lazy"
                  className="w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section data-testid="values-section" className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-36">
        <Reveal><Label>{t("about.values.label")}</Label></Reveal>
        <Reveal delay={0.1}>
          <h2 data-testid="values-title" className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
            {t("about.values.title")}
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px bg-brand-line md:grid-cols-3">
          {t("about.values.items").map((v, i) => (
            <Reveal key={i} delay={0.1 * i}>
              <div data-testid={`value-card-${i}`} className="group h-full bg-brand-black p-8 transition-colors duration-500 hover:bg-brand-surface md:p-10">
                <span className="font-display text-5xl font-black text-white/10 transition-colors duration-500 group-hover:text-brand-yellow/40">
                  0{i + 1}
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">{v.t}</h3>
                <p className="mt-4 text-sm leading-relaxed text-neutral-400 md:text-base">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-12">
          <Link
            to="/services"
            data-testid="about-cta"
            className="group inline-flex items-center gap-2 bg-brand-yellow px-7 py-4 text-sm font-bold uppercase tracking-wider text-brand-black transition-colors hover:bg-yellow-300"
          >
            {t("about.cta")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
